import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { normalizeSizeLabel, variantUpdateMatchesProduct } from './size-fit-utils.js';

const UNIT_STORAGE_KEY = 'arth:size-fit:unit';
const VALID_UNITS = new Set(['cm', 'in']);

class SizeFit extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.onVariantUpdate = this.onVariantUpdate.bind(this);
    this.onUnitClick = this.onUnitClick.bind(this);
    this.onDrawerClick = this.onDrawerClick.bind(this);
    this.onDrawerClose = this.onDrawerClose.bind(this);
    this.drawer = this.querySelector('dialog.size-fit__drawer');

    this.addEventListener('click', this.onUnitClick);
    this.addEventListener('click', this.onDrawerClick);
    this.drawer?.addEventListener('close', this.onDrawerClose);
    document.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);

    this.restoreUnit();
    this.updateSelectedSize(this.dataset.selectedSize, false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onUnitClick);
    this.removeEventListener('click', this.onDrawerClick);
    this.drawer?.removeEventListener('close', this.onDrawerClose);
    document.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
    super.disconnectedCallback();
  }

  onUnitClick(event) {
    const button = event.target.closest('[data-size-fit-unit]');
    if (!button || !this.contains(button)) return;
    this.setUnit(button.dataset.sizeFitUnit, true);
  }

  restoreUnit() {
    if (!this.querySelector('[data-size-fit-unit]')) {
      this.setUnit(this.dataset.unit, false);
      return;
    }

    let savedUnit = null;
    try {
      savedUnit = window.localStorage.getItem(UNIT_STORAGE_KEY);
    } catch (_error) {
      savedUnit = null;
    }
    this.setUnit(VALID_UNITS.has(savedUnit) ? savedUnit : this.dataset.unit, false);
  }

  setUnit(unit, announce) {
    if (!VALID_UNITS.has(unit)) return;
    const previousUnit = this.dataset.unit;
    const nextUnit = unit;
    this.dataset.unit = nextUnit;
    for (const button of this.querySelectorAll('[data-size-fit-unit]')) {
      button.setAttribute('aria-pressed', String(button.dataset.sizeFitUnit === nextUnit));
    }
    if (announce) {
      try {
        window.localStorage.setItem(UNIT_STORAGE_KEY, nextUnit);
      } catch (_error) {
        // Storage is optional; visible unit switching still works.
      }
      if (previousUnit !== nextUnit) {
        this.emitAnalytics('size-fit:unit-change', { previousUnit, nextUnit });
      }
    }
  }

  onVariantUpdate(event) {
    const eventProductId = event.detail?.data?.productId;
    if (!variantUpdateMatchesProduct(this.dataset.productId, eventProductId)) return;

    const positionAttribute = this.dataset.sizeOptionPosition;
    if (positionAttribute === undefined || positionAttribute.trim() === '') return;
    const position = Number(positionAttribute);
    const options = event.detail?.resource?.options;
    if (!Number.isInteger(position) || !Array.isArray(options)) return;
    this.updateSelectedSize(options[position], true);
  }

  updateSelectedSize(selectedSize, announce) {
    if (this.dataset.highlightSelectedSize !== 'true') {
      this.clearSelectedSizes();
      return;
    }

    const normalizedSelected = normalizeSizeLabel(selectedSize);
    let hasSemanticMatch = false;
    for (const cell of this.querySelectorAll('[data-size]')) {
      const matches =
        normalizedSelected !== '' && normalizeSizeLabel(cell.dataset.size) === normalizedSelected;
      if (matches) cell.setAttribute('data-size-fit-selected', 'true');
      else cell.removeAttribute('data-size-fit-selected');

      if (cell.hasAttribute('data-size-fit-current-target')) {
        if (matches) {
          cell.setAttribute('aria-current', 'true');
          hasSemanticMatch = true;
        } else {
          cell.removeAttribute('aria-current');
        }
      }
    }

    if (announce && hasSemanticMatch) {
      this.emitAnalytics('size-fit:size-highlight', { size: normalizedSelected });
    }
  }

  clearSelectedSizes() {
    for (const cell of this.querySelectorAll('[data-size-fit-selected], [aria-current]')) {
      cell.removeAttribute('data-size-fit-selected');
      if (cell.hasAttribute('data-size-fit-current-target')) cell.removeAttribute('aria-current');
    }
  }

  onDrawerClick(event) {
    const trigger = event.target.closest('.size-fit__drawer-trigger');
    if (!trigger || !this.contains(trigger)) return;
    this.emitAnalytics('size-fit:drawer-open', {
      hasChart: this.dataset.drawerHasChart === 'true',
      hasGuide: this.dataset.drawerHasGuide === 'true',
    });
  }

  onDrawerClose() {
    this.emitAnalytics('size-fit:drawer-close');
  }

  emitAnalytics(name, detail = {}) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail: { productId: this.dataset.productId, ...detail },
      })
    );
  }
}

if (!customElements.get('size-fit')) customElements.define('size-fit', SizeFit);
